import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  UploadCloud,
  Smartphone,
  Lock,
  FileArchive,
  CheckCircle2,
  Clock,
  Hash,
  HardDrive,
  Loader2,
  ShieldAlert,
  Download,
  Globe,
  BarChart3,
} from "lucide-react";
import localApi, { LOCAL_API } from "@/lib/localApi";
import { Switch } from "@/components/ui/switch";

const MAX_MB = 100;

export default function AppAdmin() {
  const [authed, setAuthed] = useState(false);
  const [passcode, setPasscode] = useState(sessionStorage.getItem("nd_apk_secret") || "");
  const [checking, setChecking] = useState(false);
  const [version, setVersion] = useState(null);
  const [stats, setStats] = useState(null);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const [form, setForm] = useState({
    app_name: "NearbyDeals",
    version_name: "",
    version_code: "",
    release_notes: "",
    force_update: false,
  });

  const loadVersion = useCallback(async () => {
    try {
      const { data } = await localApi.get("/app/version");
      setVersion(data?.exists ? data : null);
      if (data?.exists) {
        setForm((f) => ({ ...f, app_name: data.app_name || f.app_name }));
      }
    } catch {
      /* ignore */
    }
  }, []);

  const loadStats = useCallback(async (secret) => {
    if (!secret) return;
    try {
      const { data } = await localApi.get("/app/stats", {
        headers: { "X-Admin-Secret": secret },
      });
      setStats(data);
    } catch {
      setStats(null);
    }
  }, []);

  useEffect(() => {
    loadVersion();
    // auto-restore session
    const s = sessionStorage.getItem("nd_apk_secret");
    if (s) {
      setAuthed(true);
      loadStats(s);
    }
  }, [loadVersion, loadStats]);

  const login = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      await localApi.post("/app/admin/login", { passcode });
      sessionStorage.setItem("nd_apk_secret", passcode);
      setAuthed(true);
      loadStats(passcode);
      toast.success("Welcome, admin");
    } catch {
      toast.error("Invalid passcode");
    } finally {
      setChecking(false);
    }
  };

  const pickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".apk")) {
      toast.error("Only .apk files are allowed");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_MB} MB limit`);
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const upload = () => {
    if (!file) return toast.error("Select an APK file first");
    if (!form.version_name || !form.version_code) {
      return toast.error("Version name and code are required");
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("app_name", form.app_name);
    fd.append("version_name", form.version_name);
    fd.append("version_code", String(parseInt(form.version_code, 10) || 0));
    fd.append("release_notes", form.release_notes);
    fd.append("force_update", form.force_update ? "true" : "false");

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${LOCAL_API}/app/upload`);
    xhr.setRequestHeader("X-Admin-Secret", passcode);
    setUploading(true);
    setProgress(0);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = () => {
      setUploading(false);
      if (xhr.status === 200) {
        toast.success("APK published successfully");
        setFile(null);
        setForm((f) => ({ ...f, version_name: "", version_code: "", release_notes: "", force_update: false }));
        loadVersion();
        loadStats(passcode);
      } else {
        let msg = "Upload failed";
        try {
          const d = JSON.parse(xhr.responseText);
          msg = typeof d.detail === "string" ? d.detail : msg;
        } catch { /* ignore */ }
        if (xhr.status === 401) {
          msg = "Session expired — enter passcode again";
          sessionStorage.removeItem("nd_apk_secret");
          setAuthed(false);
        }
        toast.error(msg);
      }
    };
    xhr.onerror = () => {
      setUploading(false);
      toast.error("Network error during upload");
    };
    xhr.send(fd);
  };

  // ---------------- Login gate ----------------
  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center bg-secondary px-5">
        <form onSubmit={login} className="w-full max-w-sm bg-card rounded-3xl border border-border p-8" data-testid="apk-login">
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-primary text-primary-foreground arch mx-auto mb-6">
            <Lock className="w-6 h-6" />
          </span>
          <h1 className="font-display font-extrabold text-2xl tracking-tight text-center">App release admin</h1>
          <p className="text-muted-foreground text-center text-sm mt-2 mb-6">Enter the admin passcode to manage the Android app.</p>
          <input
            data-testid="apk-passcode"
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Admin passcode"
            className="w-full rounded-2xl bg-secondary border border-border px-4 py-3.5 font-medium focus:outline-none focus:border-primary transition-colors"
          />
          <button
            data-testid="apk-login-submit"
            disabled={checking}
            className="mt-4 w-full rounded-full bg-primary text-primary-foreground font-semibold py-3.5 hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unlock"}
          </button>
          <Link to="/" className="block text-center text-sm text-muted-foreground hover:text-primary mt-5">← Back to site</Link>
        </form>
      </div>
    );
  }

  // ---------------- Dashboard ----------------
  return (
    <div className="min-h-screen bg-secondary/60 py-10 px-5">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-11 h-11 rounded-2xl bg-primary text-primary-foreground arch">
              <Smartphone className="w-5 h-5" />
            </span>
            <div>
              <h1 className="font-display font-extrabold text-2xl tracking-tight">App Release Manager</h1>
              <p className="text-sm text-muted-foreground">Upload &amp; version-control the NearbyDeals Android app.</p>
            </div>
          </div>
          <Link to="/" className="text-sm font-semibold hover:text-primary">← Site</Link>
        </div>

        {/* Downloads & installs */}
        <div className="bg-card rounded-3xl border border-border p-6 mb-6" data-testid="apk-stats">
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" /> Downloads &amp; installs
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard
              icon={Download}
              label="APK downloads"
              value={stats?.apk_downloads ?? 0}
              testid="stat-apk-downloads"
            />
            <StatCard
              icon={Globe}
              label="PWA installs"
              value={stats?.pwa_installs ?? 0}
              testid="stat-pwa-installs"
              hint={
                stats?.pwa_by_platform
                  ? Object.entries(stats.pwa_by_platform)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(" · ")
                  : null
              }
            />
            <StatCard
              icon={Smartphone}
              label="Total installs"
              value={(stats?.apk_downloads ?? 0) + (stats?.pwa_installs ?? 0)}
              testid="stat-total-installs"
            />
          </div>

          {/* 7-day sparkline */}
          {stats?.last_7_days?.length ? (
            <div className="mt-6" data-testid="stat-sparkline">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-3">
                Last 7 days
              </p>
              <Sparkline data={stats.last_7_days} />
            </div>
          ) : null}
        </div>

        {/* Current status */}
        <div className="bg-card rounded-3xl border border-border p-6 mb-6" data-testid="apk-status">
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-success" /> Current live version
          </h2>
          {version ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Info icon={Smartphone} label="App" value={version.app_name} />
              <Info icon={Hash} label="Version" value={`${version.version_name} (${version.version_code})`} />
              <Info icon={HardDrive} label="Size" value={version.file_size_mb != null ? (version.file_size_mb < 0.1 ? "< 0.1 MB" : `${version.file_size_mb} MB`) : "—"} />
              <Info icon={Clock} label="Updated" value={version.updated_at ? new Date(version.updated_at).toLocaleString() : "—"} />
              {version.force_update && (
                <div className="sm:col-span-2 lg:col-span-4 flex items-center gap-2 text-sm font-semibold text-primary">
                  <ShieldAlert className="w-4 h-4" /> Force-update is ON for this release
                </div>
              )}
              {version.release_notes && (
                <p className="sm:col-span-2 lg:col-span-4 text-sm text-muted-foreground border-t border-border pt-3">
                  <span className="font-semibold text-foreground">What&apos;s new: </span>{version.release_notes}
                </p>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground">No APK published yet. Upload the first build below.</p>
          )}
        </div>

        {/* Uploader */}
        <div className="bg-card rounded-3xl border border-border p-6">
          <h2 className="font-display font-bold text-lg mb-4">Publish a new build</h2>

          <div
            data-testid="apk-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${
              dragging ? "border-primary bg-primary/5" : "border-border bg-secondary/50 hover:border-primary/50"
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              className="hidden"
              data-testid="apk-file-input"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileArchive className="w-8 h-8 text-primary" />
                <div className="text-left">
                  <p className="font-semibold">{file.name}</p>
                  <p className="text-sm text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              </div>
            ) : (
              <>
                <UploadCloud className="w-10 h-10 text-primary mx-auto mb-3" />
                <p className="font-semibold">Drag &amp; drop your .apk here</p>
                <p className="text-sm text-muted-foreground mt-1">or click to browse · max {MAX_MB} MB</p>
              </>
            )}
          </div>

          {uploading && (
            <div className="mt-4" data-testid="apk-progress">
              <div className="flex justify-between text-sm font-semibold mb-1">
                <span>Uploading…</span><span>{progress}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-primary transition-[width] duration-200" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <Field label="App name" value={form.app_name} onChange={(v) => setForm({ ...form, app_name: v })} testid="apk-app-name" />
            <Field label="Version name (e.g. v1.0.2)" value={form.version_name} onChange={(v) => setForm({ ...form, version_name: v })} testid="apk-version-name" />
            <Field label="Version code (integer)" type="number" value={form.version_code} onChange={(v) => setForm({ ...form, version_code: v })} testid="apk-version-code" />
            <div className="flex items-center justify-between rounded-2xl bg-secondary border border-border px-4 py-3">
              <div>
                <p className="font-semibold text-sm">Force update</p>
                <p className="text-xs text-muted-foreground">Require users to update</p>
              </div>
              <Switch data-testid="apk-force-update" checked={form.force_update} onCheckedChange={(v) => setForm({ ...form, force_update: v })} />
            </div>
          </div>

          <div className="mt-4">
            <Label>Release notes / What&apos;s new</Label>
            <textarea
              data-testid="apk-release-notes"
              rows={3}
              value={form.release_notes}
              onChange={(e) => setForm({ ...form, release_notes: e.target.value })}
              placeholder="• Bug fixes and performance improvements…"
              className="w-full rounded-2xl bg-secondary border border-border px-4 py-3 font-medium focus:outline-none focus:border-primary transition-colors resize-none"
            />
          </div>

          <button
            data-testid="apk-upload-submit"
            onClick={upload}
            disabled={uploading}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-3.5 hover:bg-primary/90 transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
            {uploading ? "Publishing…" : "Publish build"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 bg-secondary rounded-2xl p-4">
      <span className="grid place-items-center w-10 h-10 rounded-xl bg-card text-primary shrink-0">
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{label}</p>
        <p className="font-semibold truncate">{value}</p>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <div data-testid={testid} className="bg-secondary/70 rounded-2xl p-5 border border-border">
      <div className="flex items-center justify-between">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary">
          <Icon className="w-5 h-5" />
        </span>
        <span className="font-display font-extrabold text-4xl tabular-nums">
          {Number(value || 0).toLocaleString()}
        </span>
      </div>
      <p className="mt-3 text-xs uppercase tracking-wider text-muted-foreground font-bold">{label}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground capitalize">{hint}</p>}
    </div>
  );
}

function Sparkline({ data }) {
  const max = Math.max(1, ...data.map((d) => (d.apk || 0) + (d.pwa || 0)));
  return (
    <div className="flex items-end gap-2 h-24">
      {data.map((d) => {
        const total = (d.apk || 0) + (d.pwa || 0);
        const apkPct = total ? (d.apk / max) * 100 : 0;
        const pwaPct = total ? (d.pwa / max) * 100 : 0;
        const label = new Date(d.date).toLocaleDateString(undefined, { weekday: "short" });
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex flex-col justify-end h-16">
              <div style={{ height: `${pwaPct}%` }} className="w-full bg-primary rounded-t-sm" title={`PWA: ${d.pwa}`} />
              <div style={{ height: `${apkPct}%` }} className="w-full bg-[#4A8A3E]" title={`APK: ${d.apk}`} />
            </div>
            <span className="text-[10px] font-semibold text-muted-foreground">{label}</span>
            <span className="text-xs font-bold tabular-nums">{total}</span>
          </div>
        );
      })}
      <div className="ml-3 flex flex-col justify-end gap-1.5 pb-6">
        <span className="flex items-center gap-1.5 text-xs">
          <span className="w-3 h-3 rounded-sm bg-primary" /> PWA
        </span>
        <span className="flex items-center gap-1.5 text-xs">
          <span className="w-3 h-3 rounded-sm bg-[#4A8A3E]" /> APK
        </span>
      </div>
    </div>
  );
}
function Label({ children }) {
  return <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">{children}</label>;
}
function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl bg-secondary border border-border px-4 py-3 font-medium focus:outline-none focus:border-primary transition-colors"
      />
    </div>
  );
}
